import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import WorkflowDiagram from './WorkflowDiagram';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Render-Label: Bevorzugt den vom Admin gesetzten Anzeigenamen; intern wird
// aber weiter mit dem echten Modellnamen (m.name) gearbeitet, weil Ollama
// nur den kennt.
function formatModelLabel(m) {
  const size = m.size ? ` (${(m.size / 1e9).toFixed(1)} GB)` : '';
  return m.displayName ? `${m.displayName}${size}` : `${m.name}${size}`;
}

export default function WorkflowRunner({ workflow, onClose, defaultModel, initialTab }) {
  const { token } = useAuth();
  const { tenantFetch, activeTenant } = useTenant();
  const [tab, setTab] = useState(initialTab === 'history' ? 'history' : 'run');
  const [inputJson, setInputJson] = useState('{}');
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [models, setModels] = useState([]);
  const [model, setModel] = useState(defaultModel || '');

  // Verlauf
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null); // full run with step outputs

  // Live-Status pro Schritt während eines laufenden Runs (kommt vom SSE-Stream).
  // Map: { stepId: 'running' | 'completed' | 'failed' | 'skipped' }.
  // Wird gelöscht, sobald der Run komplett zurück ist (dann zeigt das Diagramm
  // die finalen Stati aus run.steps).
  const [liveStatus, setLiveStatus] = useState({});

  useEffect(() => {
    fetch(`${API_BASE}/api/models`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = d.models || [];
        setModels(list);
        if (!model && list.length) setModel(list[0].name);
      })
      .catch(() => {});
  }, [token]);

  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const res = await tenantFetch(`/api/workflows/${workflow._id}/runs`);
      const data = await res.json();
      if (res.ok) setRuns(data.runs || []);
    } finally {
      setRunsLoading(false);
    }
  }, [tenantFetch, workflow._id]);

  useEffect(() => {
    if (tab === 'history') loadRuns();
  }, [tab, loadRuns]);

  const handleRun = async () => {
    setError('');
    let parsedInput = {};
    try { parsedInput = JSON.parse(inputJson || '{}'); }
    catch { setError('Eingabe ist kein gültiges JSON.'); return; }

    setRunning(true);
    setRun(null);
    setLiveStatus({}); // alle Schritte starten als pending im Diagramm

    try {
      // Wir brauchen Streaming → fetch direkt statt tenantFetch (das liefert
      // Response zurück, aber wir wollen die Body als ReadableStream lesen).
      const res = await fetch(`${API_BASE}/api/workflows/${workflow._id}/run-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-tenant-id': activeTenant?._id || ''
        },
        body: JSON.stringify({ input: parsedInput, model })
      });
      if (!res.ok || !res.body) {
        // Fehler vor dem Stream: JSON parsen, Fehlermeldung anzeigen
        let msg = `Fehler ${res.status}`;
        try { const j = await res.json(); msg = j.error || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      // SSE-Parser: Events sind durch \n\n getrennt, Datenzeile beginnt mit "data: "
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf('\n\n')) !== -1) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const line = chunk.split('\n').find(l => l.startsWith('data: '));
          if (!line) continue;
          let ev;
          try { ev = JSON.parse(line.slice(6)); } catch { continue; }
          if (ev.type === 'step') {
            // Per-Step-Update — Diagramm-Knoten wechselt sofort die Farbe
            setLiveStatus(prev => ({ ...prev, [ev.stepId]: ev.status }));
          } else if (ev.type === 'done') {
            setRun(ev.run);
          } else if (ev.type === 'error') {
            throw new Error(ev.error || 'Engine-Fehler');
          }
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setRunning(false);
    }
  };

  const openRunDetail = async (runId) => {
    if (expandedRun?._id === runId) {
      setExpandedRun(null);
      return;
    }
    try {
      const res = await tenantFetch(`/api/workflows/${workflow._id}/runs/${runId}`);
      const data = await res.json();
      if (res.ok) setExpandedRun(data.run);
    } catch { /* ignore */ }
  };

  const triggerLabel = (t) => {
    if (t === 'schedule') return '⏱ Zeitplan';
    if (t === 'manual') return '👤 Manuell';
    return t || '–';
  };

  // Lookup: aus dem in der Run gespeicherten Modellnamen den Anzeigenamen holen
  const modelLabelFromName = (name) => {
    if (!name) return '';
    const m = models.find(x => x.name === name);
    return m?.displayName || name;
  };

  // Status pro Schritt fürs Diagramm. Reihenfolge der Quellen:
  //   1. expandedRun (Verlauf-Tab) — historische Ansicht, hat Vorrang
  //   2. liveStatus (während Run, vom SSE-Stream) — der eigentliche Live-Vorteil
  //   3. run (final, nach 'done'-Event) — falls liveStatus geleert wurde
  //   4. nichts → alle 'pending'
  const runStatus = useMemo(() => {
    if (expandedRun && Array.isArray(expandedRun.steps)) {
      const map = {};
      for (const s of expandedRun.steps) map[s.stepId] = s.status;
      for (const s of (workflow.steps || [])) {
        if (!(s.id in map)) map[s.id] = 'pending';
      }
      return map;
    }
    // Während eines Live-Runs: liveStatus hat Vorrang. Schritte ohne Eintrag
    // sind noch nicht dran → 'pending'.
    if (running || Object.keys(liveStatus).length > 0) {
      const map = {};
      for (const s of (workflow.steps || [])) {
        map[s.id] = liveStatus[s.id] || 'pending';
      }
      return map;
    }
    if (run && Array.isArray(run.steps)) {
      const map = {};
      for (const s of run.steps) map[s.stepId] = s.status;
      for (const s of (workflow.steps || [])) {
        if (!(s.id in map)) map[s.id] = 'pending';
      }
      return map;
    }
    const map = {};
    for (const s of (workflow.steps || [])) map[s.id] = 'pending';
    return map;
  }, [workflow.steps, running, run, expandedRun, liveStatus]);

  return (
    <div className="workflow-runner-overlay" onClick={onClose}>
      <div className="workflow-runner" onClick={e => e.stopPropagation()}>
        <div className="workflow-editor-header">
          <h2>Ausführen: {workflow.name}</h2>
          <button className="icon-btn" onClick={onClose}>✕</button>
        </div>

        <div className="workflow-runner-tabs">
          <button
            className={`workflow-runner-tab ${tab === 'run' ? 'active' : ''}`}
            onClick={() => setTab('run')}
          >▶ Ausführen</button>
          <button
            className={`workflow-runner-tab ${tab === 'history' ? 'active' : ''}`}
            onClick={() => setTab('history')}
          >📜 Verlauf</button>
        </div>

        <div className="workflow-editor-body">
          {tab === 'run' && (
            <>
              <label className="data-source-field">
                <span>KI-Modell (für KI-Schritte)</span>
                <select value={model} onChange={e => setModel(e.target.value)}>
                  {models.length === 0 && <option value="">— kein Modell verfügbar —</option>}
                  {models.map(m => (
                    <option key={m.name} value={m.name}>
                      {formatModelLabel(m)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="data-source-field">
                <span>Eingabe (JSON)</span>
                <textarea className="text-input" rows={4} value={inputJson} onChange={e => setInputJson(e.target.value)} />
              </label>
              {error && <div className="form-error">{error}</div>}

              {/* Diagramm-Live-Ansicht: vor dem Run alles pending, während dem Run pulsiert orange,
                  nach dem Run sieht man auf einen Blick wo's klemmte. */}
              {(workflow.steps?.length || 0) > 0 && (
                <div className="workflow-runner-diagram">
                  <WorkflowDiagram steps={workflow.steps} runStatus={runStatus} />
                </div>
              )}

              {running && !run && (
                <div className="workflow-run-status running">
                  <span className="workflow-run-spinner" /> Workflow läuft… ({workflow.steps?.length || 0} Schritt(e))
                </div>
              )}

              {run && (
                <div className="workflow-run-results">
                  <div className={`workflow-run-status ${run.status}`}>
                    {run.status === 'completed' ? '✓ Erfolgreich' : '✗ Fehlgeschlagen'}
                    {run.error && <span> — {run.error}</span>}
                  </div>
                  <ol className="workflow-run-steps">
                    {run.steps?.map((s, i) => (
                      <li key={i} className={`workflow-run-step ${s.status}`}>
                        <div className="workflow-run-step-head">
                          <span className="workflow-step-index">{i + 1}</span>
                          <span className="workflow-run-step-name">{s.name || s.stepId}</span>
                          <span className="workflow-step-type-tag">{s.type}</span>
                          <span className="workflow-run-step-ms">{s.ms} ms</span>
                        </div>
                        {s.error ? (
                          <pre className="workflow-run-error">{s.error}</pre>
                        ) : (
                          <pre className="workflow-run-output">{JSON.stringify(s.output, null, 2)}</pre>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </>
          )}

          {tab === 'history' && (
            <div className="workflow-history">
              <div className="workflow-history-head">
                <span>{runsLoading ? 'Lädt…' : `${runs.length} Lauf(e)`}</span>
                <button className="link-btn" onClick={loadRuns}>↻ Aktualisieren</button>
              </div>
              {!runsLoading && runs.length === 0 && (
                <p className="workflow-empty">Noch keine Läufe.</p>
              )}
              <ul className="workflow-history-list">
                {runs.map(r => (
                  <li key={r._id} className={`workflow-history-item ${r.status}`}>
                    <div
                      className="workflow-history-row"
                      onClick={() => openRunDetail(r._id)}
                    >
                      <span className={`workflow-history-status ${r.status}`}>
                        {r.status === 'completed' ? '✓' : '✗'}
                      </span>
                      <span className="workflow-history-time">
                        {new Date(r.startedAt).toLocaleString()}
                      </span>
                      <span className="workflow-history-trigger">{triggerLabel(r.triggerType)}</span>
                      <span className="workflow-history-meta">
                        {r.stepCount} Schritt(e)
                        {r.model ? ` · ${modelLabelFromName(r.model)}` : ''}
                        {r.finishedAt && r.startedAt
                          ? ` · ${Math.round((new Date(r.finishedAt) - new Date(r.startedAt)))} ms`
                          : ''}
                      </span>
                      <span className="workflow-history-toggle">
                        {expandedRun?._id === r._id ? '▾' : '▸'}
                      </span>
                    </div>
                    {r.error && (
                      <div className="workflow-history-error">{r.error}</div>
                    )}
                    {expandedRun?._id === r._id && (
                      <div className="workflow-history-detail">
                        <ol className="workflow-run-steps">
                          {(expandedRun.steps || []).map((s, i) => (
                            <li key={i} className={`workflow-run-step ${s.status}`}>
                              <div className="workflow-run-step-head">
                                <span className="workflow-step-index">{i + 1}</span>
                                <span className="workflow-run-step-name">{s.name || s.stepId}</span>
                                <span className="workflow-step-type-tag">{s.type}</span>
                                <span className="workflow-run-step-ms">{s.ms} ms</span>
                              </div>
                              {s.error ? (
                                <pre className="workflow-run-error">{s.error}</pre>
                              ) : (
                                <pre className="workflow-run-output">{JSON.stringify(s.output, null, 2)}</pre>
                              )}
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="workflow-editor-footer">
          <button className="link-btn" onClick={onClose}>Schließen</button>
          {tab === 'run' && (
            <button className="primary-btn" onClick={handleRun} disabled={running || !model}>
              {running ? 'Läuft…' : '▶ Workflow ausführen'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
